import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

export interface Shortcut {
  /** `KeyboardEvent.key`, matched case-sensitively for letters. */
  key: string;
  /** What it does, in the shortcut sheet's words. */
  description: string;
  group: string;
  run: () => void;
}

const isTypingTarget = (target: EventTarget | null): boolean => {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  const tag = target.tagName;
  return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT";
};

/** Always available, wherever the user is. Listed first in the sheet. */
export const GLOBAL_SHORTCUTS: ReadonlyArray<Omit<Shortcut, "run">> = [
  { key: "⌘K", description: "Go to a screen", group: "Anywhere" },
  { key: "?", description: "Show these shortcuts", group: "Anywhere" },
  { key: "Esc", description: "Close a dialog, drawer or menu", group: "Anywhere" },
];

interface Registry {
  shortcuts: readonly Shortcut[];
  register: (list: readonly Shortcut[]) => () => void;
}

const ShortcutContext = createContext<Registry | null>(null);

/**
 * Holds whatever the current screen has registered, so the shortcut sheet can
 * be a single global dialog rather than one per page.
 *
 * The registry exists for one reason: (H) doc 27 §9 wants a **discoverable**
 * shortcut sheet, and a sheet maintained separately from the key bindings
 * drifts on the first change. Registering the list that binds the keys makes
 * "documented but broken" and "works but undiscoverable" both impossible.
 */
export function ShortcutRegistryProvider({ children }: { children: ReactNode }) {
  const [scoped, setScoped] = useState<readonly Shortcut[]>([]);

  const register = useCallback((list: readonly Shortcut[]) => {
    setScoped(list);
    return () => {
      setScoped((current) => (current === list ? [] : current));
    };
  }, []);

  const value = useMemo<Registry>(() => ({ shortcuts: scoped, register }), [scoped, register]);
  return <ShortcutContext.Provider value={value}>{children}</ShortcutContext.Provider>;
}

export function useShortcutRegistry(): Registry {
  const ctx = useContext(ShortcutContext);
  if (ctx === null) {
    throw new Error("Wrap the app in <ShortcutRegistryProvider> before using shortcuts.");
  }
  return ctx;
}

/**
 * doc 27 §9: (H) every high-volume workflow completable by keyboard alone,
 * with a discoverable shortcut sheet. The exception queue is used for hours a
 * day; mouse dependence there is a product failure, not an a11y footnote.
 *
 * Shortcuts are declared as data, bound here, and rendered into the sheet from
 * the same list.
 */
export function useShortcuts(shortcuts: readonly Shortcut[], enabled = true): void {
  const { register } = useShortcutRegistry();

  useEffect(() => register(shortcuts), [register, shortcuts]);

  useEffect(() => {
    if (!enabled) return;
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      if (isTypingTarget(event.target)) return;
      const match = shortcuts.find((s) => s.key === event.key);
      if (!match) return;
      event.preventDefault();
      match.run();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [shortcuts, enabled]);
}
